// frontend/src/pages/Reflections.js
import React, { useState, useEffect } from 'react';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { 
    LightBulbIcon, 
    CheckCircleIcon, 
    PencilSquareIcon,
    GlobeAltIcon
} from '@heroicons/react/24/outline';

const Reflections = () => {
    const [loading, setLoading] = useState(true);
    const [templates, setTemplates] = useState([]);
    const [reflections, setReflections] = useState([]);
    const [activeTemplate, setActiveTemplate] = useState(null);
    const [answers, setAnswers] = useState([]);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const [tempRes, refRes] = await Promise.all([
                axios.get('/evidence/reflection/templates'),
                axios.get('/evidence/reflection/my-reflections')
            ]);
            setTemplates(tempRes.data.data);
            setReflections(refRes.data.data);
        } catch (error) {
            toast.error('Failed to load reflections');
        } finally {
            setLoading(false);
        }
    };

    const handleStartReflection = (template) => {
        setActiveTemplate(template);
        const questions = typeof template.questions === 'string' ? JSON.parse(template.questions) : template.questions;
        setAnswers(new Array(questions.length).fill(''));
    };

    const handleSubmitReflection = async (e) => {
        e.preventDefault();
        try {
            await axios.post('/evidence/reflection', {
                templateId: activeTemplate.id,
                answers: answers
            });
            toast.success('Reflection saved successfully!');
            setActiveTemplate(null);
            loadData();
        } catch (error) {
            toast.error('Failed to save reflection');
        }
    };

    const handlePublishReflection = async (reflectionId) => {
        try {
            await axios.post(`/evidence/reflection/${reflectionId}/publish`, { visibility: 'public' });
            toast.success('Reflection published!');
            loadData();
        } catch (error) {
            toast.error('Failed to publish reflection');
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading reflections...</div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-900">Reflections & Growth</h1>
                <p className="text-gray-600 text-sm mt-1">
                    Structured reflections on your learning, milestones, and project experiences
                </p>
            </div>

            {/* Prompt Templates */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
                    <LightBulbIcon className="w-5 h-5 text-yellow-500" />
                    <span>Available Reflection Prompts</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {templates.map((tpl) => (
                        <div key={tpl.id} className="border border-gray-200 rounded-lg p-4 flex flex-col justify-between">
                            <div>
                                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 font-medium capitalize text-gray-700">
                                    {tpl.trigger_type.replace('_', ' ')}
                                </span>
                                <h3 className="font-semibold text-sm text-gray-900 mt-2">{tpl.name}</h3>
                                <p className="text-xs text-gray-500 mt-1">{tpl.description}</p>
                            </div>
                            <button
                                onClick={() => handleStartReflection(tpl)}
                                className="mt-4 w-full py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800 flex items-center justify-center space-x-1"
                            >
                                <PencilSquareIcon className="w-4 h-4" />
                                <span>Reflect Now</span>
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* Completed Reflections */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">My Completed Reflections</h2>
                {reflections.length === 0 ? (
                    <p className="text-sm text-gray-500">No reflections submitted yet.</p>
                ) : (
                    <div className="space-y-4">
                        {reflections.map((ref) => (
                            <div key={ref.id} className="border border-gray-200 rounded-lg p-4 space-y-2">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h3 className="font-semibold text-sm text-gray-900">{ref.title}</h3>
                                        <p className="text-xs text-gray-400">
                                            Created: {new Date(ref.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className={`text-xs px-2 py-0.5 rounded font-medium capitalize ${
                                            ref.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {ref.status}
                                        </span>
                                        {ref.status !== 'published' && (
                                            <button
                                                onClick={() => handlePublishReflection(ref.id)}
                                                className="px-2.5 py-1 bg-black text-white text-xs rounded hover:bg-gray-800 flex items-center space-x-1"
                                            >
                                                <GlobeAltIcon className="w-3.5 h-3.5" />
                                                <span>Publish</span>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Reflection Answers Modal */}
            {activeTemplate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <form onSubmit={handleSubmitReflection} className="bg-white rounded-lg p-6 max-w-lg w-full space-y-4 max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-bold text-gray-900">{activeTemplate.name}</h3>
                        <p className="text-xs text-gray-500">{activeTemplate.description}</p>

                        {(typeof activeTemplate.questions === 'string' 
                            ? JSON.parse(activeTemplate.questions) 
                            : activeTemplate.questions
                        ).map((q, idx) => (
                            <div key={idx} className="space-y-1">
                                <label className="block text-xs font-semibold text-gray-700">
                                    Q{idx + 1}: {q}
                                </label>
                                <textarea
                                    required
                                    rows={2}
                                    value={answers[idx] || ''}
                                    onChange={(e) => {
                                        const newAnswers = [...answers];
                                        newAnswers[idx] = e.target.value;
                                        setAnswers(newAnswers);
                                    }}
                                    className="w-full p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-black"
                                    placeholder="Your answer..."
                                />
                            </div>
                        ))}

                        <div className="flex justify-end space-x-2 pt-2 border-t border-gray-200">
                            <button type="button" onClick={() => setActiveTemplate(null)} className="px-3 py-1.5 text-xs text-gray-600">Cancel</button>
                            <button type="submit" className="px-4 py-1.5 bg-black text-white text-xs font-medium rounded hover:bg-gray-800">Save Reflection</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Reflections;
